import { doitSignalerMascotte, type EtatSautMascotte } from './sautMascotte.js';
import { forwardRef, useEffect, useRef, useState, type JSX } from 'react';
import { IconAI, LiaHead, Pastille, cn } from '@taki/ui';

export type LiaLauncherStyle = 'mascot' | 'bubble';
export type LiaUnread = 'ready' | 'error' | null;

export interface LiaLauncherProps {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  pending: boolean;
  unread: LiaUnread;
  launcher: LiaLauncherStyle;
  totalPastilles?: number;
  arrivees?: number;
  mascotteAnimee?: boolean;
}

export type CatMood = 'idle' | 'curious' | 'happy' | 'alert';

const BODY = 'M17 66V10Q17 3 22 6L38 28Q43 26 49 26h17q6 0 11 2L93 6q5-3 5 4v56H17Z';
const PAWS = 'M23 64q0-8 12-8t12 8v5q0 7-12 7t-12-7v-5Zm42 0q0-8 12-8t12 8v5q0 7-12 7t-12-7v-5Z';

/**
 * Le chat regarde vers la gauche, vers l'app, sans suivre la souris. Au focus
 * clavier, un liseré épouse sa silhouette au lieu d'encadrer le bouton.
 */
export function LiaCat({ mood, nombre = 0 }: { mood: CatMood; nombre?: number }): JSX.Element {
  const eyeY = mood === 'curious' ? 47 : 49;
  const pupilY = mood === 'curious' ? 43 : 50;
  return <svg viewBox="0 0 112 82" className="h-[4.5rem] w-[6rem] overflow-visible" aria-hidden="true" focusable="false">
    <g className="lia-mascotte-corps">
    <g className="opacity-0 transition-opacity duration-150 group-focus-visible:opacity-100 motion-reduce:transition-none" fill="none" strokeLinejoin="round">
      <path d={`${BODY}${PAWS}`} stroke="var(--ink-900)" strokeWidth="9" />
      <path d={`${BODY}${PAWS}`} stroke="var(--surface)" strokeWidth="4.5" />
    </g>
    <g stroke="var(--accent)" strokeWidth="2.4" strokeLinecap="round" fill="none">
      <path d="M19 57 4 53M19 64 3 65M93 57l15-4M93 64l16 1" />
    </g>
    <path fill="var(--accent)" d={BODY} />
    {mood === 'happy' ? <g fill="none" stroke="var(--accent-label)" strokeWidth="2.5" strokeLinecap="round"><path d="M35 48q7-10 14 0M63 48q7-10 14 0" /></g> : <>
      <g fill="white"><ellipse cx="42" cy={eyeY} rx="9" ry="11" /><ellipse cx="70" cy={eyeY} rx="9" ry="11" /></g>
      <g fill="var(--ink-900)"><ellipse cx="38.5" cy={pupilY} rx="3.4" ry="6.4" /><ellipse cx="66.5" cy={pupilY} rx="3.4" ry="6.4" /></g>
    </>}
    <path fill="var(--accent)" stroke="var(--surface)" strokeWidth="2" d={PAWS} />
    {mood === 'curious' ? <g fill="none" stroke="var(--ink-900)" strokeWidth="2.3" strokeLinecap="round"><path d="m48 13-3-5M56 11V4m8 9 3-5" /></g> : null}
    {mood === 'alert' ? <text x="52" y="19" fill="var(--ink-900)" fontSize="22" fontWeight="800">!</text> : null}
    </g>
    {nombre > 0 && <g transform="translate(72 -28) rotate(-8 24 18)">
      <g className="lia-mascotte-bulle">
      <path d="M13 1C5 1 0 6 0 14v9c0 8 5 13 13 13h5l-4 8c-.5 1.5 1 2.5 2.5 1.5L29 36h7c8 0 13-5 13-13V13C49 5 44 0 36 0Z"
        fill="var(--ink-900)" />
      <text className="lia-mascotte-compteur" x="24.5" y="19" dominantBaseline="central" textAnchor="middle" fill="var(--surface)" fontSize={nombre > 99 ? 18 : nombre > 9 ? 22 : 26} fontWeight="700">
        {nombre > 99 ? '99+' : nombre}
      </text>
      </g>
    </g>}
  </svg>;
}

export function liaLauncherLabel(pending: boolean, unread: LiaUnread): string {
  return pending ? 'Ask Lia, Lia réfléchit'
    : unread === 'error' ? 'Ask Lia, réponse indisponible'
      : unread === 'ready' ? 'Ask Lia, réponse prête' : 'Ask Lia';
}

/* La tete du chat vit dans @taki/ui (`packages/ui/src/lia/LiaHead.tsx`) : Canaux la montre aussi. */
export { LiaHead };

/** Bouton bas droit. Le chat reste contenu dans sa hitbox. */
export const LiaLauncher = forwardRef<HTMLButtonElement, LiaLauncherProps>(function LiaLauncher({
  open, onOpen, onClose, pending, unread, launcher, totalPastilles = 0, arrivees = 0, mascotteAnimee = true,
}, ref) {
  const avant = useRef<EtatSautMascotte | null>(null);
  const [saut, setSaut] = useState(0);
  const [sautActif, setSautActif] = useState(false);
  useEffect(() => {
    if (!saut) return;
    const arret = setTimeout(() => setSautActif(false), 500);
    return () => clearTimeout(arret);
  }, [saut]);
  useEffect(() => {
    const constater = (): EtatSautMascotte => ({ total: totalPastilles, arrivees, visible: !open && !document.hidden, animee: mascotteAnimee, mouvementReduit: matchMedia('(prefers-reduced-motion: reduce)').matches });
    const apres = constater();
    if (doitSignalerMascotte(avant.current, apres)) { setSaut(n => n + 1); setSautActif(true); }
    else if (!apres.visible || !apres.animee) setSautActif(false);
    avant.current = apres;
    const synchroniser = () => { avant.current = constater(); };
    document.addEventListener('visibilitychange', synchroniser);
    window.addEventListener('online', synchroniser);
    return () => { document.removeEventListener('visibilitychange', synchroniser); window.removeEventListener('online', synchroniser); };
  }, [totalPastilles, arrivees, open, mascotteAnimee]);
  const [engaged, setEngaged] = useState(false);
  const mood: CatMood = pending ? 'curious' : unread === 'error' ? 'alert' : totalPastilles > 0 ? 'happy' : engaged ? 'curious' : 'idle';
  const bulleDeParole = launcher === 'mascot' && totalPastilles > 0;

  useEffect(() => { if (open) setEngaged(false); }, [open]);

  // La hauteur du lanceur, publiee pour que les listes gardent leur derniere ligne visible au-dessus.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--taki-launcher-clearance', launcher === 'bubble' ? '4.25rem' : bulleDeParole ? '7.5rem' : '5.75rem');
    return () => { root.style.removeProperty('--taki-launcher-clearance'); };
  }, [launcher, bulleDeParole]);

  const label = `${liaLauncherLabel(pending, unread)}${totalPastilles > 0 ? `, ${totalPastilles} messages non lus` : ''}`;

  return <button ref={ref} type="button" onClick={open ? onClose : onOpen} tabIndex={open ? -1 : 0}
    aria-label={label} aria-expanded={open} aria-hidden={open || undefined} aria-controls="taki-chat-panel" title={open ? undefined : label}
    onPointerEnter={(event) => { if (event.pointerType === 'mouse') setEngaged(true); }}
    onPointerLeave={() => setEngaged(false)}
    data-lia-state={mood} data-lia-launcher={launcher}
    className={cn('group pointer-events-auto absolute bottom-[max(0.75rem,env(safe-area-inset-bottom))] right-[max(0.75rem,env(safe-area-inset-right))] flex items-center justify-center outline-none [-webkit-tap-highlight-color:transparent] transition-[opacity,transform] duration-200 motion-reduce:transition-none motion-reduce:transform-none sm:bottom-5 sm:right-5',
      launcher === 'bubble' ? 'h-11 w-11 rounded-full border border-accent-border bg-accent text-accent-label shadow-panel hover:-translate-y-0.5 focus-visible:shadow-ring' : 'h-[4.75rem] w-[6.25rem] bg-transparent hover:-translate-y-0.5',
      open && 'pointer-events-none translate-y-2 opacity-0')}>
    {launcher === 'bubble' ? <><IconAI size={20} />{(pending || unread) && <span aria-hidden="true"
      className={cn('absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-surface',
        unread === 'error' ? 'bg-danger' : 'bg-accent-dot', pending && 'animate-pulse motion-reduce:animate-none')} />}</>
      : <span key={saut} className={sautActif && mascotteAnimee && !open ? 'lia-nouvelle-arrivee' : undefined}>
        <LiaCat mood={mood} nombre={totalPastilles} />
      </span>}
    {launcher === 'bubble' && <Pastille nombre={totalPastilles} className="absolute right-0 top-0 border-2 border-surface" />}
  </button>;
});
